<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-2">
      <h6 class="mb-0">Items</h6>
      <button
        type="button"
        class="btn btn-sm btn-outline-primary"
        data-test="merge-selected"
        :disabled="!canMergeChecked"
        title="Select at least two items with the same unit price and VAT %"
        @click="mergeChecked"
      >
        Merge selected ({{ checkedItems.length }})
      </button>
    </div>

    <div
      v-for="group in rowGroups"
      :key="group.itemIds[0]"
      class="card border mb-2"
      data-test="row-group"
    >
      <div class="card-body py-2">
        <div class="d-flex gap-2 align-items-center">
          <div class="flex-grow-1">
            <input
              type="text"
              class="form-control form-control-sm"
              :class="{ 'is-invalid': errorOf(group) }"
              :value="descriptionOf(group)"
              :maxlength="ROW_GROUP_DESCRIPTION_MAX_LENGTH"
              @input="
                updateGroupDescription(
                  group,
                  ($event.target as HTMLInputElement).value
                )
              "
            />
            <div v-if="errorOf(group)" class="invalid-feedback">
              {{ errorOf(group) }}
            </div>
          </div>
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary"
            data-test="unmerge"
            @click="unmerge(group)"
          >
            Unmerge
          </button>
        </div>
        <small class="text-muted">
          {{ group.itemIds.length }} items ·
          {{ sumBy(groupMembers(group), "amount") }} MWh ·
          {{ formatMoney(sumBy(groupMembers(group), "total_with_vat")) }} €
          incl. VAT
        </small>
      </div>
    </div>

    <DataTable
      v-model:filters="filters"
      v-model:selection="checkedItems"
      :value="ungroupedItems"
      dataKey="id"
      filterDisplay="row"
      class="border rounded mb-2"
      :scrollable="ungroupedItems.length > 20"
      :scrollHeight="ungroupedItems.length > 20 ? '400px' : undefined"
    >
      <template #empty>
        <div class="text-center py-3 text-muted">All items are merged</div>
      </template>
      <Column selectionMode="multiple" :headerStyle="{ width: '3rem' }" />
      <Column
        field="description"
        header="Description"
        :showFilterMatchModes="false"
        :showFilterMenu="false"
      >
        <template #filter="{ filterModel, filterCallback }">
          <InputText
            v-model="filterModel.value"
            type="text"
            class="p-column-filter w-100"
            placeholder="Search..."
            @input="filterCallback()"
          />
        </template>
      </Column>
      <Column
        field="item_type"
        header="Type"
        :showFilterMatchModes="false"
        :showFilterMenu="false"
      >
        <template #body="{ data }">{{
          formatItemType(data.item_type)
        }}</template>
        <template #filter="{ filterModel, filterCallback }">
          <MultiSelect
            v-model="filterModel.value"
            :options="itemTypeOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="All"
            appendTo="body"
            class="p-column-filter w-100"
            @change="filterCallback()"
          />
        </template>
      </Column>
      <Column field="amount" header="Qty" bodyClass="text-end" />
      <Column header="Unit Price" bodyClass="text-end">
        <template #body="{ data }">{{ data.unit_price.toFixed(4) }} €</template>
      </Column>
      <Column header="VAT %" bodyClass="text-end">
        <template #body="{ data }"
          >{{ ((data.vat_fraction ?? 0) * 100).toFixed(0) }}%</template
        >
      </Column>
      <Column header="Total (incl. VAT)" bodyClass="text-end">
        <template #body="{ data }"
          >{{ formatMoney(data.total_with_vat) }} €</template
        >
      </Column>
    </DataTable>
  </div>
</template>

<script setup lang="ts">
import { FilterMatchMode } from "@primevue/core/api";
import { keyBy, map, sumBy, uniq } from "lodash-es";
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import InputText from "primevue/inputtext";
import MultiSelect from "primevue/multiselect";
import { computed, ref } from "vue";

import { formatMoney } from "@/composables/formatUtils";

import {
  canMerge,
  groupDescription,
  groupDescriptionError,
  type InvoiceLanguage,
  ROW_GROUP_DESCRIPTION_MAX_LENGTH,
  type RowGroupDraft,
} from "./rowGroups";
import type { PreviewItem } from "./types";
import { formatItemType } from "./utils";

const props = defineProps<{
  items: PreviewItem[];
  language: InvoiceLanguage;
}>();

const rowGroups = defineModel<RowGroupDraft[]>("rowGroups", { required: true });

const checkedItems = ref<PreviewItem[]>([]);
const filters = ref({
  description: {
    value: null as string | null,
    matchMode: FilterMatchMode.CONTAINS,
  },
  item_type: { value: null as string[] | null, matchMode: FilterMatchMode.IN },
});

const itemsById = computed(() => keyBy(props.items, "id"));
const groupedItemIds = computed(
  () => new Set(rowGroups.value.flatMap((group) => group.itemIds))
);
const ungroupedItems = computed(() =>
  props.items.filter((item) => !groupedItemIds.value.has(item.id))
);
const itemTypeOptions = computed(() =>
  uniq(map(props.items, "item_type"))
    .sort()
    .map((value) => ({ label: formatItemType(value), value }))
);
const canMergeChecked = computed(() => canMerge(checkedItems.value));

const groupMembers = (group: RowGroupDraft) =>
  group.itemIds.map((id) => itemsById.value[id]);

const descriptionOf = (group: RowGroupDraft) =>
  groupDescription(group, groupMembers(group), props.language);

const errorOf = (group: RowGroupDraft) =>
  groupDescriptionError(descriptionOf(group));

const mergeChecked = () => {
  if (!canMergeChecked.value) {
    return;
  }
  rowGroups.value = [
    ...rowGroups.value,
    { itemIds: map(checkedItems.value, "id"), customDescription: null },
  ];
  checkedItems.value = [];
};

const unmerge = (group: RowGroupDraft) => {
  rowGroups.value = rowGroups.value.filter((other) => other !== group);
};

const updateGroupDescription = (group: RowGroupDraft, description: string) => {
  rowGroups.value = rowGroups.value.map((other) =>
    other === group ? { ...other, customDescription: description } : other
  );
};
</script>
